import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormLabel,
} from '@mui/material';
import { uniqueId } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useBatchUpdateDealsMutation } from '@/features/deals/dealsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { showToast, hideToast } from '@/features/toast/toastSlice';
import { setSelected } from '@/features/table/dealsTableSlice';
import { MAX_TABLE_SELECTION } from '@/components/table/data';
import { getObjectType, buildBulkFailureMessage } from '@/utils/constants';
import { DealStageSelect } from '@/components/selects/DealStageSelect';

export const BulkChangeDealStageDialog = ({ open, onClose, pipeline }) => {
	const dispatch = useDispatch();
	const { selected } = useSelector((state) => state.dealTable);
	const { username: email } = useSelector((state) => state.msalAccount.account);

	const isDebtorPipeline = pipeline === process.env.REACT_APP_PIPELINE_DEBTORS;
	const isClaimsPipeline = pipeline === process.env.REACT_APP_PIPELINE_CLAIMS;
	const allowBulkClose = isDebtorPipeline || isClaimsPipeline;
	const objectLabel = getObjectType(
		isDebtorPipeline ? 'debtor' : isClaimsPipeline ? 'ticket' : 'deal',
	);

	const [targetStage, setTargetStage] = useState(null);

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [batchUpdateDeals, { isLoading: isSubmitting }] = useBatchUpdateDealsMutation();

	const handleClose = () => {
		if (isSubmitting) return;
		onClose();
		setTargetStage(null);
	};

	const buildStageProperties = (newStage) => {
		const isClosingDeal = newStage.completed || newStage.closed;

		return {
			dealStage: newStage.id,
			...(isClosingDeal && {
				...(newStage.completed && {
					completed: true,
					completedBy: userDetails.userId,
				}),
				closed: true,
				closedBy: userDetails.userId,
				dateClosed: new Date().getTime(),
			}),
			...(!isClosingDeal && {
				completed: false,
				closed: false,
				dateClosed: null,
				completedBy: null,
				closedBy: null,
			}),
		};
	};

	const handleConfirm = async () => {
		if (!targetStage || selected.length === 0 || !userDetails || isSubmitting) {
			return;
		}

		if (selected.length > MAX_TABLE_SELECTION) {
			dispatch(
				showToast({
					message: `Select at most ${MAX_TABLE_SELECTION} ${objectLabel}s at a time.`,
					error: true,
				}),
			);
			return;
		}

		const requestedCount = selected.length;
		const dealIds = selected.map((deal) => deal.dealId);
		const toastId = uniqueId('bulk-stage');

		dispatch(setSelected([]));
		onClose();
		setTargetStage(null);

		dispatch(
			showToast({
				id: toastId,
				message: `Updating ${requestedCount} ${objectLabel}${
					requestedCount > 1 ? 's' : ''
				}…`,
				autohide: false,
				loading: true,
			}),
		);

		try {
			const res = await batchUpdateDeals({
				dealIds,
				properties: buildStageProperties(targetStage),
			}).unwrap();

			dispatch(hideToast(toastId));
			const updatedCount = res?.updated?.length ?? 0;
			const failed = res?.failed ?? [];

			if (failed.length > 0) {
				dispatch(
					showToast({
						message: buildBulkFailureMessage(failed),
						error: true,
					}),
				);
				return;
			}

			dispatch(
				showToast({
					message: `Successfully updated stage for ${updatedCount || requestedCount} ${objectLabel}${
						(updatedCount || requestedCount) > 1 ? 's' : ''
					}.`,
					success: true,
				}),
			);
		} catch (e) {
			console.error('Error bulk updating deal stage', e);
			dispatch(hideToast(toastId));
			dispatch(
				showToast({
					message: e?.data?.message ?? e?.message ?? 'Failed to update deal stage',
					error: true,
				}),
			);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
			<DialogTitle>
				{`Change stage for ${selected.length} ${objectLabel}${
					selected.length > 1 ? 's' : ''
				}`}
			</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<FormControl fullWidth sx={{ mt: 1 }}>
					<FormLabel>
						{isClaimsPipeline
							? 'Claim Status'
							: isDebtorPipeline
								? 'Debtor Stage'
								: 'Deal Stage'}
					</FormLabel>
					<DealStageSelect
						pipeline={pipeline}
						stage={targetStage}
						onChange={setTargetStage}
						disabled={isSubmitting}
						disableClosedStages={!allowBulkClose}
					/>
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSubmitting} color='primary'>
					Cancel
				</Button>
				<Button
					color='primary'
					variant='contained'
					disabled={isSubmitting || !targetStage}
					onClick={handleConfirm}
				>
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
	);
};
